import { useMemo, useState } from "react";
import { Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { useLayout } from "@/hooks/use-layout";
import { useAuth } from "@/hooks/use-auth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Loader2, Users, AlertTriangle, ChevronDown, ChevronRight, Search, ExternalLink,
  Clock, CalendarClock, ArrowRight, History, Crown, Download, UserCheck, FileSpreadsheet, FileText, Gauge, Building2, Target,
} from "lucide-react";

// ─── Tab 1 (existing) — HR Overdue Dossier ───────────────────────────────────

type DossierItem = {
  id: string;
  ref: string | null;
  title: string;
  due_date: string | null;
  original_due_date: string | null;
  days_overdue: number;
  priority: string | null;
  authority_lane: string | null;
  status: string;
  current_responsible: string;
  assigned_to: string;
};

type Holder = {
  name: string;
  count: number;
  oldest_overdue_days: number;
  priority_breakdown: { high: number; medium: number; low: number; unspecified: number };
  items: DossierItem[];
};

type DossierResponse = {
  as_of: string;
  total_overdue: number;
  total_holders: number;
  caller_name: string;
  holders: Holder[];
};

function priorityClass(p: string | null): string {
  const v = (p || "").toLowerCase();
  if (v === "high") return "bg-red-500/20 text-red-300 border-red-500/30";
  if (v === "medium") return "bg-amber-500/20 text-amber-300 border-amber-500/30";
  if (v === "low") return "bg-emerald-500/20 text-emerald-300 border-emerald-500/30";
  return "bg-white/10 text-white/70 border-white/20";
}

function ageClass(days: number): string {
  if (days >= 30) return "bg-red-500/20 text-red-300 border-red-500/30";
  if (days >= 7) return "bg-amber-500/20 text-amber-300 border-amber-500/30";
  return "bg-orange-500/20 text-orange-300 border-orange-500/30";
}

function statusClass(s: string): string {
  if (s === "WITH_SDF_MD_REVIEW") return "bg-purple-500/20 text-purple-300 border-purple-500/30";
  if (s === "SIGNED_OFF") return "bg-emerald-500/20 text-emerald-300 border-emerald-500/30";
  if (s === "IN_PROGRESS") return "bg-cyan-500/20 text-cyan-300 border-cyan-500/30";
  if (s === "AWAITING_INFO") return "bg-amber-500/20 text-amber-300 border-amber-500/30";
  if (s === "AWAITING_DISPATCH") return "bg-indigo-500/20 text-indigo-300 border-indigo-500/30";
  if (s === "SENT_UPWARD") return "bg-blue-500/20 text-blue-300 border-blue-500/30";
  if (s === "CLOSED") return "bg-emerald-500/20 text-emerald-300 border-emerald-500/30";
  return "bg-white/10 text-white/70 border-white/20";
}

function formatDate(iso: string | null): string {
  if (!iso) return "\u2014";
  // Accepts both bare YYYY-MM-DD calendar strings (e.g. due dates from
  // the DB DATE columns) and full ISO timestamps (e.g. slice_started_at /
  // slice_ended_at on the Employee Dossier rows, which are .toISOString()
  // outputs of the underlying handoff event timestamps). Without this
  // branch a full ISO string would have "T00:00:00" naively appended,
  // producing "2026-05-08T13:42:00.000ZT00:00:00" → Invalid Date in the
  // Holding-period column.
  const d = /^\d{4}-\d{2}-\d{2}$/.test(iso)
    ? new Date(iso + "T00:00:00")
    : new Date(iso);
  if (Number.isNaN(d.getTime())) return "\u2014";
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function formatDateTime(iso: string | null): string {
  if (!iso) return "\u2014";
  return new Date(iso).toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

// v2.9.0 — short "12 May 08:00"-style label for stepped chain badges,
// matching the format spelled out in the task spec.
function formatChainMoment(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

function useHasPerm(name: string): boolean {
  const { hasPermission } = useAuth();
  return hasPermission(name);
}

function OverdueDossierTab() {
  const canSeeEmployeeDossier = useHasPerm("view_employee_dossier");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState("");
  const [allOpen, setAllOpen] = useState(false);

  const { data, isLoading, error } = useQuery<DossierResponse>({
    queryKey: ["/api/hr/overdue-dossier"],
  });

  const filteredHolders = useMemo(() => {
    if (!data) return [] as Holder[];
    const q = search.trim().toLowerCase();
    if (!q) return data.holders;
    return data.holders
      .map((h) => {
        const holderMatch = h.name.toLowerCase().includes(q);
        const matchedItems = h.items.filter((it) =>
          it.title.toLowerCase().includes(q) ||
          (it.ref || "").toLowerCase().includes(q) ||
          (it.authority_lane || "").toLowerCase().includes(q) ||
          it.assigned_to.toLowerCase().includes(q),
        );
        if (holderMatch) return h;
        if (matchedItems.length === 0) return null;
        const breakdown = { high: 0, medium: 0, low: 0, unspecified: 0 };
        let oldest = 0;
        for (const it of matchedItems) {
          const p = (it.priority || "").toLowerCase();
          if (p === "high") breakdown.high++;
          else if (p === "medium") breakdown.medium++;
          else if (p === "low") breakdown.low++;
          else breakdown.unspecified++;
          if (it.days_overdue > oldest) oldest = it.days_overdue;
        }
        return {
          ...h,
          items: matchedItems,
          count: matchedItems.length,
          oldest_overdue_days: oldest,
          priority_breakdown: breakdown,
        };
      })
      .filter((h): h is Holder => h !== null);
  }, [data, search]);

  const globalOldestOverdue = useMemo(() => {
    if (!data || data.holders.length === 0) return 0;
    let max = 0;
    for (const h of data.holders) if (h.oldest_overdue_days > max) max = h.oldest_overdue_days;
    return max;
  }, [data]);

  const isOpen = (name: string) => allOpen || expanded.has(name);
  const toggle = (name: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-3 text-white/60 py-8" data-testid="status-loading">
        <Loader2 className="w-5 h-5 animate-spin" /> Loading overdue dossier…
      </div>
    );
  }

  if (error) {
    return (
      <Card className="bg-red-500/10 border-red-500/30">
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 text-red-300" data-testid="status-error">
            <AlertTriangle className="w-5 h-5" />
            Failed to load dossier. {(error as Error).message}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold text-white tracking-tight" data-testid="text-overdue-heading">
            Items currently past their deadline
          </h2>
          <p className="text-sm text-white/60 mt-1 max-w-2xl">
            Grouped by who is holding each item right now. Drill into any row to open the full responsibility timeline.
          </p>
        </div>
        <div className="text-xs text-white/40 font-mono" data-testid="text-as-of">
          As of {new Date(data.as_of).toLocaleString()}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card className="bg-white/5 border-white/10">
          <CardContent className="pt-5 pb-4 text-center">
            <div className="text-xs uppercase tracking-wide text-white/50">Items overdue</div>
            <div className="text-3xl font-semibold text-white mt-1" data-testid="text-total-overdue">{data.total_overdue}</div>
          </CardContent>
        </Card>
        <Card className="bg-white/5 border-white/10">
          <CardContent className="pt-5 pb-4 text-center">
            <div className="text-xs uppercase tracking-wide text-white/50">Holders affected</div>
            <div className="text-3xl font-semibold text-white mt-1" data-testid="text-total-holders">{data.total_holders}</div>
          </CardContent>
        </Card>
        <Card className="bg-white/5 border-white/10">
          <CardContent className="pt-5 pb-4 text-center">
            <div className="text-xs uppercase tracking-wide text-white/50">Oldest overdue</div>
            <div className="text-3xl font-semibold text-white mt-1" data-testid="text-oldest-overdue">
              {globalOldestOverdue}
              <span className="text-sm font-normal text-white/50 ml-1">days</span>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Filter by holder, item, lane, or assignee…"
            className="pl-9 bg-white/5 border-white/10 text-white placeholder:text-white/40"
            data-testid="input-search"
          />
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setAllOpen((v) => !v)}
          className="border-white/20 text-white/80 hover:bg-white/10"
          data-testid="button-toggle-all"
        >
          {allOpen ? "Collapse all" : "Expand all"}
        </Button>
      </div>

      {filteredHolders.length === 0 ? (
        <Card className="bg-emerald-500/10 border-emerald-500/30">
          <CardContent className="pt-6 pb-6 text-center text-emerald-300" data-testid="status-empty">
            {data.total_overdue === 0
              ? "No items are currently overdue. Everything is on time."
              : "No holders match your search."}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredHolders.map((holder) => {
            const open = isOpen(holder.name);
            return (
              <Card key={holder.name} className="bg-white/5 border-white/10" data-testid={`card-holder-${holder.name}`}>
                <CardHeader
                  className="cursor-pointer hover:bg-white/5 transition-colors py-3 px-4"
                  onClick={() => toggle(holder.name)}
                  data-testid={`button-toggle-holder-${holder.name}`}
                >
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <CardTitle className="text-base font-medium text-white flex items-center gap-2">
                      {open ? <ChevronDown className="w-4 h-4 text-white/60" /> : <ChevronRight className="w-4 h-4 text-white/60" />}
                      {canSeeEmployeeDossier ? (
                        <Link
                          href={`/hr-dossier?tab=employee-dossier&person=${encodeURIComponent(holder.name)}`}
                          onClick={(e) => e.stopPropagation()}
                          className="text-cyan-300 hover:text-cyan-200 hover:underline"
                          data-testid={`link-employee-dossier-${holder.name}`}
                          title="Open Employee Dossier for this person"
                        >
                          {holder.name}
                        </Link>
                      ) : (
                        holder.name
                      )}
                      <Badge className="bg-red-500/20 text-red-300 border-red-500/30 ml-2" data-testid={`badge-count-${holder.name}`}>
                        {holder.count} overdue
                      </Badge>
                    </CardTitle>
                    <div className="flex items-center gap-2 flex-wrap text-xs">
                      {holder.priority_breakdown.high > 0 && (
                        <Badge className={priorityClass("high")}>High: {holder.priority_breakdown.high}</Badge>
                      )}
                      {holder.priority_breakdown.medium > 0 && (
                        <Badge className={priorityClass("medium")}>Med: {holder.priority_breakdown.medium}</Badge>
                      )}
                      {holder.priority_breakdown.low > 0 && (
                        <Badge className={priorityClass("low")}>Low: {holder.priority_breakdown.low}</Badge>
                      )}
                      {holder.priority_breakdown.unspecified > 0 && (
                        <Badge className={priorityClass(null)}>Unspec: {holder.priority_breakdown.unspecified}</Badge>
                      )}
                      <Badge className={ageClass(holder.oldest_overdue_days)}>
                        <Clock className="w-3 h-3 mr-1" /> Oldest {holder.oldest_overdue_days}d
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
                {open && (
                  <CardContent className="pt-0 pb-3 px-4">
                    <div className="overflow-x-auto rounded border border-white/10">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="bg-white/5 text-left text-white/60 uppercase tracking-wide">
                            <th className="px-3 py-2 font-medium">Item</th>
                            <th className="px-3 py-2 font-medium">Lane</th>
                            <th className="px-3 py-2 font-medium">Originally</th>
                            <th className="px-3 py-2 font-medium">Due</th>
                            <th className="px-3 py-2 font-medium text-right">Days late</th>
                            <th className="px-3 py-2 font-medium">Priority</th>
                            <th className="px-3 py-2 font-medium">Status</th>
                            <th className="px-3 py-2 font-medium"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {holder.items.map((it) => (
                            <tr
                              key={it.id}
                              className="border-t border-white/5 hover:bg-white/5"
                              data-testid={`row-item-${it.id}`}
                            >
                              <td className="px-3 py-2 text-white/90 max-w-md">
                                <div className="font-mono text-xs text-white/40">{it.ref || it.id.slice(0, 8)}</div>
                                <div className="truncate" title={it.title}>{it.title}</div>
                              </td>
                              <td className="px-3 py-2 text-white/70">{it.authority_lane || "\u2014"}</td>
                              <td className="px-3 py-2 text-white/70">{it.assigned_to}</td>
                              <td className="px-3 py-2 text-white/70 whitespace-nowrap">{formatDate(it.due_date)}</td>
                              <td className="px-3 py-2 text-right">
                                <Badge className={ageClass(it.days_overdue)}>{it.days_overdue}d</Badge>
                              </td>
                              <td className="px-3 py-2">
                                <Badge className={priorityClass(it.priority)}>{it.priority || "\u2014"}</Badge>
                              </td>
                              <td className="px-3 py-2">
                                <Badge className={statusClass(it.status)}>{it.status.replace(/_/g, " ")}</Badge>
                              </td>
                              <td className="px-3 py-2 text-right whitespace-nowrap">
                                <Link
                                  href={`/action-items/${it.id}`}
                                  className="inline-flex items-center gap-1 text-cyan-300 hover:text-cyan-200 text-xs"
                                  data-testid={`link-detail-${it.id}`}
                                >
                                  Open <ExternalLink className="w-3 h-3" />
                                </Link>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Tab 2 (new) — MD Review History ─────────────────────────────────────────

type ChainEntry = { label: string; scheduled_for: string; at: string };

type HistoryItem = {
  id: string;
  ref: string | null;
  title: string;
  owner: string;
  authority_lane: string | null;
  status: string;
  meeting_status: "open" | "scheduled" | "held" | "cancelled" | "closed";
  requested_at: string;
  chain: ChainEntry[];
  outcome: { kind: "scheduled" | "held" | "cancelled" | "none"; at: string | null };
  closed_at: string | null;
  holding_working_days: number;
  sla_verdict: "green" | "amber" | "red";
  awaiting_dispatch_at: string | null;
  dispatched_at: string | null;
  dispatch_lag_wd: number | null;
};

type HistoryResponse = {
  as_of: string;
  sla_threshold_wd: number;
  sla_amber_upper_wd: number;
  total: number;
  items: HistoryItem[];
  filters: {
    owners: string[];
    statuses: string[];
  };
};

function verdictBadgeClass(v: "green" | "amber" | "red"): string {
  if (v === "green") return "bg-emerald-500/20 text-emerald-300 border-emerald-500/30";
  if (v === "amber") return "bg-amber-500/20 text-amber-300 border-amber-500/30";
  return "bg-red-500/20 text-red-300 border-red-500/30";
}

function meetingStatusBadgeClass(s: HistoryItem["meeting_status"]): string {
  if (s === "scheduled") return "bg-cyan-500/20 text-cyan-300 border-cyan-500/30";
  if (s === "held") return "bg-emerald-500/20 text-emerald-300 border-emerald-500/30";
  if (s === "cancelled") return "bg-red-500/20 text-red-300 border-red-500/30";
  if (s === "open") return "bg-purple-500/20 text-purple-300 border-purple-500/30";
  return "bg-white/10 text-white/70 border-white/20";
}

function MdReviewHistoryTab() {
  const [from, setFrom] = useState<string>("");
  const [to, setTo] = useState<string>("");
  const [sla, setSla] = useState<string>("all");
  const [owner, setOwner] = useState<string>("all");
  const [status, setStatus] = useState<string>("all");

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    if (from) params.set("from", from);
    if (to) params.set("to", to);
    if (sla && sla !== "all") params.set("sla", sla);
    if (owner && owner !== "all") params.set("owner", owner);
    if (status && status !== "all") params.set("status", status);
    const qs = params.toString();
    return qs ? `?${qs}` : "";
  }, [from, to, sla, owner, status]);

  const { data, isLoading, error } = useQuery<HistoryResponse>({
    queryKey: ["/api/md-review/history", from, to, sla, owner, status],
    queryFn: async () => {
      const res = await fetch(`/api/md-review/history${queryString}`, { credentials: "include" });
      if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
      return res.json();
    },
  });

  // Filter dropdown options come from a metadata-only query (no filters
  // applied) so the dropdowns don't disappear after a filter narrows the
  // result set to nothing.
  const { data: meta } = useQuery<HistoryResponse>({
    queryKey: ["/api/md-review/history", "__filters__"],
    queryFn: async () => {
      const res = await fetch(`/api/md-review/history`, { credentials: "include" });
      if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
      return res.json();
    },
  });

  const ownerOptions = meta?.filters.owners ?? [];
  const statusOptions = meta?.filters.statuses ?? [];

  function clearFilters() {
    setFrom(""); setTo(""); setSla("all"); setOwner("all"); setStatus("all");
  }

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="max-w-3xl">
          <h2 className="text-lg font-semibold text-white tracking-tight flex items-center gap-2" data-testid="text-md-history-heading">
            <Crown className="w-5 h-5 text-purple-300" />
            How senior review handled these items
          </h2>
          <p className="text-sm text-white/60 mt-1">
            Each row is one item that SDF PMO sent up for SDF MD review. Holding time and any
            reschedules below are <span className="text-white/80">attributed to the MD review tier</span>,
            not to the original assignee — this view is intended to show how senior leadership has
            been handling its in-tray. The soft target is{" "}
            <span className="text-white/80 font-medium">
              {meta?.sla_threshold_wd ?? 10} working day{(meta?.sla_threshold_wd ?? 10) === 1 ? "" : "s"}
            </span>{" "}
            from request to close.
          </p>
        </div>
        {data && (
          <div className="text-xs text-white/40 font-mono" data-testid="text-md-history-as-of">
            As of {new Date(data.as_of).toLocaleString()}
          </div>
        )}
      </div>

      <Card className="bg-white/5 border-white/10">
        <CardContent className="pt-4 pb-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3 items-end">
            <div className="flex flex-col gap-1">
              <label className="text-xs uppercase tracking-wide text-white/50">Request from</label>
              <Input
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                className="bg-white/5 border-white/10 text-white"
                data-testid="input-filter-from"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs uppercase tracking-wide text-white/50">Request to</label>
              <Input
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                className="bg-white/5 border-white/10 text-white"
                data-testid="input-filter-to"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs uppercase tracking-wide text-white/50">SLA verdict</label>
              <Select value={sla} onValueChange={setSla}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="select-filter-sla">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="green">Green (on-target)</SelectItem>
                  <SelectItem value="amber">Amber (slight delay)</SelectItem>
                  <SelectItem value="red">Red (overdue)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs uppercase tracking-wide text-white/50">Owner</label>
              <Select value={owner} onValueChange={setOwner}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="select-filter-owner">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All owners</SelectItem>
                  {ownerOptions.map((o) => (
                    <SelectItem key={o} value={o}>{o}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs uppercase tracking-wide text-white/50">Item status</label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="select-filter-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  {statusOptions.map((s) => (
                    <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex">
              <Button
                variant="outline"
                size="sm"
                onClick={clearFilters}
                className="border-white/20 text-white/80 hover:bg-white/10 w-full"
                data-testid="button-clear-filters"
              >
                Clear filters
              </Button>
            </div>
          </div>
          <div className="mt-3 pt-3 border-t border-white/5 flex items-center justify-between gap-3 flex-wrap">
            <div className="text-sm text-white/50">
              Generate a board-ready PDF using the filters above.
            </div>
            <Button
              variant="outline"
              size="sm"
              asChild
              className="border-purple-400/40 text-purple-200 hover:bg-purple-400/10 hover:text-purple-100"
              data-testid="button-download-md-history-pdf"
            >
              <a href={`/api/md-review/history.pdf${queryString}${(() => { try { const tz = Intl.DateTimeFormat().resolvedOptions().timeZone; return `${queryString ? "&" : "?"}tz=${encodeURIComponent(tz)}`; } catch { return ""; } })()}`} download>
                <Download className="w-4 h-4 mr-1.5" />
                Download PDF
              </a>
            </Button>
          </div>
        </CardContent>
      </Card>

      {isLoading ? (
        <div className="flex items-center gap-3 text-white/60 py-8" data-testid="status-history-loading">
          <Loader2 className="w-5 h-5 animate-spin" /> Loading MD review history…
        </div>
      ) : error ? (
        <Card className="bg-red-500/10 border-red-500/30">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-red-300" data-testid="status-history-error">
              <AlertTriangle className="w-5 h-5" /> Failed to load history. {(error as Error).message}
            </div>
          </CardContent>
        </Card>
      ) : !data || data.items.length === 0 ? (
        <Card className="bg-white/5 border-white/10">
          <CardContent className="pt-6 pb-6 text-center text-white/60" data-testid="status-history-empty">
            No items match the current filters.
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="text-xs text-white/50" data-testid="text-history-count">
            Showing {data.items.length} item{data.items.length === 1 ? "" : "s"} sent for SDF MD review.
          </div>
          <div className="space-y-3">
            {data.items.map((it) => {
              const reschedules = Math.max(it.chain.length - 1, 0);
              return (
                <Card
                  key={it.id}
                  className="bg-white/5 border-white/10"
                  data-testid={`card-history-${it.id}`}
                >
                  <CardContent className="pt-4 pb-4 space-y-3">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="min-w-0 flex-1">
                        <div className="font-mono text-xs text-white/40">{it.ref || it.id.slice(0, 8)}</div>
                        <div className="text-sm text-white font-medium truncate" title={it.title}>{it.title}</div>
                        <div className="text-xs text-white/50 mt-0.5">
                          Owner: <span className="text-white/80">{it.owner}</span>
                          {it.authority_lane && (
                            <>
                              {" · "}
                              <span className="text-white/80">{it.authority_lane}</span>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge className={meetingStatusBadgeClass(it.meeting_status)} data-testid={`badge-meeting-status-${it.id}`}>
                          {it.meeting_status}
                        </Badge>
                        <Badge className={statusClass(it.status)} data-testid={`badge-status-${it.id}`}>
                          {it.status.replace(/_/g, " ")}
                        </Badge>
                        <Badge className={verdictBadgeClass(it.sla_verdict)} data-testid={`badge-sla-${it.id}`}>
                          <Clock className="w-3 h-3 mr-1" />
                          {it.holding_working_days} WD
                        </Badge>
                        <Link
                          href={`/action-items/${it.id}`}
                          className="inline-flex items-center gap-1 text-cyan-300 hover:text-cyan-200 text-xs"
                          data-testid={`link-history-detail-${it.id}`}
                        >
                          Open <ExternalLink className="w-3 h-3" />
                        </Link>
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2 text-xs">
                      <Badge variant="outline" className="border-white/20 text-white/70 bg-white/[0.03]">
                        <CalendarClock className="w-3 h-3 mr-1" />
                        Sent up: {formatDateTime(it.requested_at)}
                      </Badge>

                      {it.chain.length > 0 ? (
                        it.chain.map((c, idx) => (
                          <span key={`${it.id}-c${idx}`} className="flex items-center gap-2">
                            <ArrowRight className="w-3 h-3 text-white/30" />
                            <Badge
                              variant="outline"
                              className="border-cyan-500/30 text-cyan-200 bg-cyan-500/10"
                              data-testid={`badge-chain-${it.id}-${idx}`}
                            >
                              {c.label}: {formatChainMoment(c.scheduled_for)}
                            </Badge>
                          </span>
                        ))
                      ) : (
                        <>
                          <ArrowRight className="w-3 h-3 text-white/30" />
                          <Badge variant="outline" className="border-white/20 text-white/60 bg-white/[0.03]">
                            No meeting scheduled yet
                          </Badge>
                        </>
                      )}

                      {it.outcome.kind === "held" && (
                        <>
                          <ArrowRight className="w-3 h-3 text-white/30" />
                          <Badge variant="outline" className="border-emerald-500/30 text-emerald-300 bg-emerald-500/10">
                            Held: {formatDateTime(it.outcome.at)}
                          </Badge>
                        </>
                      )}
                      {it.outcome.kind === "cancelled" && (
                        <>
                          <ArrowRight className="w-3 h-3 text-white/30" />
                          <Badge variant="outline" className="border-red-500/30 text-red-300 bg-red-500/10">
                            Cancelled: {formatDateTime(it.outcome.at)}
                          </Badge>
                        </>
                      )}
                      {it.closed_at && (
                        <>
                          <ArrowRight className="w-3 h-3 text-white/30" />
                          <Badge variant="outline" className="border-indigo-500/30 text-indigo-200 bg-indigo-500/10">
                            Left review: {formatDateTime(it.closed_at)}
                          </Badge>
                        </>
                      )}
                    </div>

                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-white/60 pt-1 border-t border-white/5">
                      <span>
                        <span className="text-white/40">Reschedules:</span>{" "}
                        <span className={reschedules > 0 ? "text-amber-300 font-medium" : "text-white/80"}>{reschedules}</span>
                      </span>
                      <span>
                        <span className="text-white/40">Holding time:</span>{" "}
                        <span className="text-white/80 font-medium">{it.holding_working_days} WD</span>
                        {data.sla_threshold_wd && (
                          <span className="text-white/40"> / target {data.sla_threshold_wd} WD</span>
                        )}
                      </span>
                      <span>
                        <span className="text-white/40">Dispatched:</span>{" "}
                        {it.dispatched_at ? (
                          <span className="text-white/80">
                            {formatDateTime(it.dispatched_at)}
                            {typeof it.dispatch_lag_wd === "number" && (
                              <span className="text-white/40"> ({it.dispatch_lag_wd} WD lag)</span>
                            )}
                          </span>
                        ) : (
                          <span className="text-white/50">not yet</span>
                        )}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Tab 3 — Employee Dossier ────────────────────────────────────────────────

type EmployeeDossierVerdict =
  | "cleared_on_time" | "cleared_late" | "still_holding_overdue" | "still_holding_in_flight"
  | "handed_off_clean" | "handed_off_late" | "received_late" | "expired_in_their_hands";

type EmployeeDossierItem = {
  id: string; ref: string | null; title: string;
  authority_lane: string | null; current_status: string;
  original_assignee: string | null;
  original_due_date: string | null; current_due_date: string | null;
  current_internal_due_date: string | null; closed_at: string | null;
  slice_started_at: string; slice_ended_at: string | null;
  slice_ended_via: "handoff" | "closed" | "still_holding";
  working_days_held: number; caused_drop: boolean;
  reschedules_caused: number; verdict: EmployeeDossierVerdict;
};

type EmployeeDossierReport = {
  person: string;
  range: { from: string; to: string };
  generated_at: string;
  aggregates: {
    total_items_touched: number; total_slices: number;
    cleared_on_time: number; cleared_late: number;
    still_holding_overdue: number; still_holding_in_flight: number;
    handed_off_clean: number; handed_off_late: number;
    received_late: number; expired_in_their_hands: number;
    avg_working_days_held: number; drop_count: number; reschedules_caused: number;
  };
  items: EmployeeDossierItem[];
};

const VERDICT_LABEL: Record<EmployeeDossierVerdict, string> = {
  cleared_on_time: "Cleared on time",
  cleared_late: "Cleared late",
  still_holding_overdue: "Still holding (overdue)",
  still_holding_in_flight: "Still holding (in flight)",
  handed_off_clean: "Handed off cleanly",
  handed_off_late: "Handed off late",
  received_late: "Received already late",
  expired_in_their_hands: "Expired in their hands",
};

const VERDICT_CHIP_CLASS: Record<EmployeeDossierVerdict, string> = {
  cleared_on_time:        "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
  handed_off_clean:       "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
  still_holding_in_flight:"bg-indigo-500/20 text-indigo-300 border-indigo-500/30",
  cleared_late:           "bg-amber-500/20 text-amber-300 border-amber-500/30",
  handed_off_late:        "bg-amber-500/20 text-amber-300 border-amber-500/30",
  received_late:          "bg-white/10 text-white/60 border-white/20",
  still_holding_overdue:  "bg-red-500/20 text-red-300 border-red-500/30",
  expired_in_their_hands: "bg-red-500/20 text-red-300 border-red-500/30",
};

type WorkloadSnapshotItem = {
  id: string; ref: string | null; title: string;
  authority_lane: string | null; status: string;
  current_due_date: string | null; current_internal_due_date: string | null;
  effective_due_date: string | null;
  is_overdue: boolean; received_from_other: boolean;
};

type WorkloadSnapshot = {
  person: string; as_of: string;
  holding: number; holding_overdue: number; oldest_overdue_days: number | null;
  received_from_other: number; originated_here: number;
  owns_open: number; owns_held_by_others: number;
  by_status: { status: string; count: number }[];
  items: WorkloadSnapshotItem[];
};

function prettyStatus(s: string): string {
  return s.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function shiftIsoDays(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

function startOfYearIso(): string {
  return `${new Date().getFullYear()}-01-01`;
}

function EmployeeDossierTab({ initialPerson, initialFrom, initialTo }: {
  initialPerson: string; initialFrom: string; initialTo: string;
}) {
  const [person, setPerson] = useState<string>(initialPerson);
  const [from, setFrom] = useState<string>(initialFrom || shiftIsoDays(365));
  const [to, setTo] = useState<string>(initialTo || todayIso());
  const [submittedKey, setSubmittedKey] = useState<string>(
    initialPerson ? `${initialPerson}|${initialFrom || shiftIsoDays(365)}|${initialTo || todayIso()}` : ""
  );

  // People picker — combobox over the existing /api/people list, which
  // returns team-member names (matches what the rest of the system uses
  // as the "holder" identity). Workflow actors are filtered out — they're
  // ceremonial roles, not individuals.
  const peopleQuery = useQuery<{ data: { name: string; is_workflow_actor: boolean }[] }>({
    queryKey: ["/api/people"],
    queryFn: async () => {
      const res = await fetch("/api/people", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load people");
      const json = await res.json();
      // /api/people returns { team_members, authority_lanes, combined, combined_meta, ... }.
      // Prefer combined_meta (has both name + is_workflow_actor); fall back defensively.
      if (Array.isArray(json?.combined_meta)) return { data: json.combined_meta };
      if (Array.isArray(json?.team_members)) return { data: json.team_members };
      if (Array.isArray(json?.data)) return json;
      if (Array.isArray(json)) return { data: json };
      return { data: [] };
    },
  });
  const peopleOptions = useMemo(() => {
    const list = peopleQuery.data?.data || [];
    return list
      .filter((p: any) => p && typeof p.name === "string" && !p.is_workflow_actor)
      .map((p: any) => p.name)
      .sort((a: string, b: string) => a.localeCompare(b));
  }, [peopleQuery.data]);

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    params.set("person", person);
    if (from) params.set("from", from);
    if (to) params.set("to", to);
    return params.toString();
  }, [person, from, to]);

  const { data, isLoading, error } = useQuery<EmployeeDossierReport>({
    queryKey: ["/api/employee-dossier", submittedKey],
    enabled: !!submittedKey,
    queryFn: async () => {
      const res = await fetch(`/api/employee-dossier?${queryString}`, { credentials: "include" });
      if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
      return res.json();
    },
  });

  // Live workload snapshot — loads as soon as a person is picked (before
  // Generate), so a manager can gauge current load before assigning work.
  const workloadQuery = useQuery<WorkloadSnapshot>({
    queryKey: ["/api/workload-snapshot", person],
    enabled: !!person,
    queryFn: async () => {
      const res = await fetch(`/api/workload-snapshot?person=${encodeURIComponent(person)}`, { credentials: "include" });
      if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
      return res.json();
    },
  });
  const workload = workloadQuery.data;

  function generate() {
    if (!person) return;
    setSubmittedKey(`${person}|${from}|${to}`);
    if (typeof window !== "undefined") {
      const u = new URL(window.location.href);
      u.searchParams.set("tab", "employee-dossier");
      u.searchParams.set("person", person);
      if (from) u.searchParams.set("from", from); else u.searchParams.delete("from");
      if (to) u.searchParams.set("to", to); else u.searchParams.delete("to");
      window.history.replaceState({}, "", u.toString());
    }
  }

  // Auto-generate on first mount if URL pre-loaded a person.
  useState(() => {
    if (initialPerson && !submittedKey) {
      setSubmittedKey(`${initialPerson}|${from}|${to}`);
    }
  });

  function presetRange(kind: "30d" | "90d" | "ytd" | "12m") {
    if (kind === "30d") { setFrom(shiftIsoDays(30)); setTo(todayIso()); }
    else if (kind === "90d") { setFrom(shiftIsoDays(90)); setTo(todayIso()); }
    else if (kind === "ytd") { setFrom(startOfYearIso()); setTo(todayIso()); }
    else if (kind === "12m") { setFrom(shiftIsoDays(365)); setTo(todayIso()); }
  }

  const downloadHref = (ext: "xlsx" | "pdf") => {
    const tz = typeof Intl !== "undefined" ? Intl.DateTimeFormat().resolvedOptions().timeZone : "";
    const params = new URLSearchParams();
    params.set("person", person);
    if (from) params.set("from", from);
    if (to) params.set("to", to);
    if (tz) params.set("tz", tz);
    return `/api/employee-dossier.${ext}?${params.toString()}`;
  };

  return (
    <div className="space-y-5">
      {/* Controls */}
      <Card className="bg-white/5 border-white/10">
        <CardContent className="p-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr_1fr_auto] gap-3 items-end">
            <div className="space-y-1.5">
              <label className="text-white/60 text-xs">Person</label>
              <Select value={person} onValueChange={setPerson}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="select-employee-dossier-person">
                  <SelectValue placeholder="Select a person…" />
                </SelectTrigger>
                <SelectContent className="max-h-80 overflow-y-auto">
                  {peopleOptions.map((n: string) => (
                    <SelectItem key={n} value={n} data-testid={`option-person-${n}`}>{n}</SelectItem>
                  ))}
                  {peopleOptions.length === 0 && (
                    <SelectItem value="__none__" disabled>No team members loaded</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <label className="text-white/60 text-xs">From</label>
              <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
                className="bg-white/5 border-white/10 text-white" data-testid="input-employee-dossier-from" />
            </div>
            <div className="space-y-1.5">
              <label className="text-white/60 text-xs">To</label>
              <Input type="date" value={to} onChange={(e) => setTo(e.target.value)}
                className="bg-white/5 border-white/10 text-white" data-testid="input-employee-dossier-to" />
            </div>
            <Button onClick={generate} disabled={!person} data-testid="button-employee-dossier-generate"
              className="bg-cyan-600 hover:bg-cyan-500 text-white">
              Generate
            </Button>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-white/40 text-xs mr-1">Quick range:</span>
            <Button size="sm" variant="outline" className="h-7 px-2 text-xs bg-white/5 border-white/10 text-white/70 hover:bg-white/10"
              onClick={() => presetRange("30d")} data-testid="button-range-30d">Last 30d</Button>
            <Button size="sm" variant="outline" className="h-7 px-2 text-xs bg-white/5 border-white/10 text-white/70 hover:bg-white/10"
              onClick={() => presetRange("90d")} data-testid="button-range-90d">Last 90d</Button>
            <Button size="sm" variant="outline" className="h-7 px-2 text-xs bg-white/5 border-white/10 text-white/70 hover:bg-white/10"
              onClick={() => presetRange("ytd")} data-testid="button-range-ytd">YTD</Button>
            <Button size="sm" variant="outline" className="h-7 px-2 text-xs bg-white/5 border-white/10 text-white/70 hover:bg-white/10"
              onClick={() => presetRange("12m")} data-testid="button-range-12m">Last 12 months</Button>
            <div className="flex-1" />
            {data && (
              <>
                <a href={downloadHref("xlsx")} target="_blank" rel="noopener noreferrer" data-testid="link-download-xlsx">
                  <Button size="sm" variant="outline" className="h-7 px-3 text-xs bg-white/5 border-white/10 text-white/80 hover:bg-white/10">
                    <FileSpreadsheet className="w-3 h-3 mr-1.5" /> Excel
                  </Button>
                </a>
                <a href={downloadHref("pdf")} target="_blank" rel="noopener noreferrer" data-testid="link-download-pdf">
                  <Button size="sm" variant="outline" className="h-7 px-3 text-xs bg-white/5 border-white/10 text-white/80 hover:bg-white/10">
                    <FileText className="w-3 h-3 mr-1.5" /> PDF
                  </Button>
                </a>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Live workload snapshot — shows the moment a person is picked */}
      {person && (
        <Card className="bg-white/5 border-white/10" data-testid="card-workload-snapshot">
          <CardHeader className="pb-2">
            <CardTitle className="text-base text-white flex items-center gap-2">
              <Gauge className="w-4 h-4 text-cyan-400" />
              Workload now — {person}
              {workload && (
                <span className="text-xs text-white/40 font-normal ml-2">as of {workload.as_of}</span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            {workloadQuery.isLoading && (
              <div className="flex items-center gap-2 text-white/50 text-sm py-4">
                <Loader2 className="w-4 h-4 animate-spin text-cyan-400" /> Checking current load…
              </div>
            )}
            {workloadQuery.error && (
              <div className="text-red-300 text-sm py-2">
                Couldn't load workload: {String((workloadQuery.error as Error).message)}
              </div>
            )}
            {workload && (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                  {[
                    { l: "Currently holding", v: workload.holding, c: "text-white", t: "holding" },
                    { l: "Holding (overdue)", v: workload.holding_overdue, c: workload.holding_overdue > 0 ? "text-red-400" : "text-white/60", t: "holding-overdue" },
                    { l: "Oldest overdue (WD)", v: workload.oldest_overdue_days ?? "—", c: "text-amber-400", t: "oldest-overdue" },
                    { l: "Received from others", v: workload.received_from_other, c: "text-indigo-400", t: "received" },
                    { l: "Owns (open)", v: workload.owns_open, c: "text-white", t: "owns" },
                    { l: "Owns, held by others", v: workload.owns_held_by_others, c: "text-white/60", t: "owns-elsewhere" },
                  ].map((s) => (
                    <div key={s.l} className="rounded border border-white/10 bg-white/5 p-3 text-center">
                      <div className="text-xs uppercase tracking-wide text-white/50">{s.l}</div>
                      <div className={`text-2xl font-semibold mt-1 ${s.c}`} data-testid={`workload-${s.t}`}>{s.v}</div>
                    </div>
                  ))}
                </div>

                {workload.by_status.length > 0 && (
                  <div className="flex flex-wrap items-center gap-2 mt-3">
                    <span className="text-white/40 text-xs mr-1">Held by status:</span>
                    {workload.by_status.map((b) => (
                      <Badge key={b.status} variant="outline" className="bg-white/5 border-white/15 text-white/70 text-xs"
                        data-testid={`workload-status-${b.status}`}>
                        {prettyStatus(b.status)} · {b.count}
                      </Badge>
                    ))}
                  </div>
                )}

                {workload.holding === 0 ? (
                  <div className="text-white/50 text-sm mt-3">Nothing currently on {person}'s plate.</div>
                ) : (
                  <div className="overflow-x-auto rounded mt-3 border border-white/10">
                    <table className="w-full text-xs" data-testid="table-workload-items">
                      <thead>
                        <tr className="bg-white/5 text-left text-white/60 uppercase tracking-wide">
                          <th className="px-3 py-2 font-medium">Item</th>
                          <th className="px-3 py-2 font-medium">Lane</th>
                          <th className="px-3 py-2 font-medium">Status</th>
                          <th className="px-3 py-2 font-medium">Effective due</th>
                          <th className="px-3 py-2 font-medium">Source</th>
                        </tr>
                      </thead>
                      <tbody>
                        {workload.items.map((it) => (
                          <tr key={it.id} className="border-t border-white/5 hover:bg-white/5" data-testid={`row-workload-${it.id}`}>
                            <td className="px-3 py-2">
                              <Link href={`/action-items/${it.id}`} className="text-cyan-300 hover:underline">
                                {it.ref ? <span className="text-white/40 mr-1">{it.ref}</span> : null}{it.title}
                              </Link>
                            </td>
                            <td className="px-3 py-2 text-white/60">{it.authority_lane || "—"}</td>
                            <td className="px-3 py-2 text-white/70">{prettyStatus(it.status)}</td>
                            <td className={`px-3 py-2 ${it.is_overdue ? "text-red-400" : "text-white/70"}`}>
                              {it.effective_due_date || "—"}{it.is_overdue ? " · overdue" : ""}
                            </td>
                            <td className="px-3 py-2">
                              {it.received_from_other
                                ? <span className="text-indigo-300">Received</span>
                                : <span className="text-white/50">Original</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Results */}
      {!submittedKey && (
        <Card className="bg-white/5 border-white/10">
          <CardContent className="p-8 text-center text-white/50">
            Pick a person and a date range, then press <strong>Generate</strong> to build the historical report.
          </CardContent>
        </Card>
      )}
      {submittedKey && isLoading && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
        </div>
      )}
      {submittedKey && error && (
        <Card className="bg-red-500/10 border-red-500/30">
          <CardContent className="p-4 text-red-300 text-sm">
            Failed to load report: {String((error as Error).message)}
          </CardContent>
        </Card>
      )}
      {data && (
        <>
          {/* Aggregates cover */}
          <Card className="bg-white/5 border-white/10">
            <CardHeader className="pb-2">
              <CardTitle className="text-base text-white flex items-center gap-2">
                <UserCheck className="w-4 h-4 text-cyan-400" />
                {data.person}
                <span className="text-xs text-white/40 font-normal ml-2">
                  {data.range.from} → {data.range.to}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                {[
                  { l: "Items touched", v: data.aggregates.total_items_touched, c: "text-white" },
                  { l: "Holding periods", v: data.aggregates.total_slices, c: "text-white" },
                  { l: "Avg WD per holding period", v: data.aggregates.avg_working_days_held, c: "text-white" },
                  { l: "Drop count", v: data.aggregates.drop_count, c: "text-red-400" },
                  { l: "Reschedules caused", v: data.aggregates.reschedules_caused, c: "text-amber-400" },
                  { l: "Cleared on time", v: data.aggregates.cleared_on_time, c: "text-emerald-400" },
                  { l: "Cleared late", v: data.aggregates.cleared_late, c: "text-amber-400" },
                  { l: "Still holding (overdue)", v: data.aggregates.still_holding_overdue, c: "text-red-400" },
                  { l: "Still holding (in flight)", v: data.aggregates.still_holding_in_flight, c: "text-indigo-400" },
                  { l: "Handed off cleanly", v: data.aggregates.handed_off_clean, c: "text-emerald-400" },
                  { l: "Handed off late", v: data.aggregates.handed_off_late, c: "text-amber-400" },
                  { l: "Received already late", v: data.aggregates.received_late, c: "text-white/60" },
                ].map((t) => (
                  <div key={t.l} className="rounded border border-white/10 bg-white/5 p-3 text-center">
                    <div className="text-xs uppercase tracking-wide text-white/50">{t.l}</div>
                    <div className={`text-2xl font-semibold mt-1 ${t.c}`} data-testid={`stat-${t.l.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>{t.v}</div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Per-item table */}
          <Card className="bg-white/5 border-white/10">
            <CardContent className="p-0">
              {data.items.length === 0 ? (
                <div className="p-8 text-center text-white/50">No holding activity in the selected period.</div>
              ) : (
                <div className="overflow-x-auto rounded">
                  <table className="w-full text-xs" data-testid="table-employee-dossier-items">
                    <thead>
                      <tr className="bg-white/5 text-left text-white/60 uppercase tracking-wide">
                        <th className="px-3 py-2 font-medium">Verdict</th>
                        <th className="px-3 py-2 font-medium">Item</th>
                        <th className="px-3 py-2 font-medium">Lane</th>
                        <th className="px-3 py-2 font-medium">Holding period</th>
                        <th className="px-3 py-2 font-medium text-right">WD held</th>
                        <th className="px-3 py-2 font-medium">Original due</th>
                        <th className="px-3 py-2 font-medium">Status</th>
                        <th className="px-3 py-2 font-medium text-center">Drop?</th>
                        <th className="px-3 py-2 font-medium"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.items.map((it) => (
                        <tr key={`${it.id}-${it.slice_started_at}`} className="border-t border-white/5 hover:bg-white/5"
                          data-testid={`row-employee-dossier-${it.id}`}>
                          <td className="px-3 py-2">
                            <Badge className={VERDICT_CHIP_CLASS[it.verdict]}>{VERDICT_LABEL[it.verdict]}</Badge>
                          </td>
                          <td className="px-3 py-2 text-white/90 max-w-md">
                            <div className="font-mono text-xs text-white/40">{it.ref || it.id.slice(0, 8)}</div>
                            <div className="truncate" title={it.title}>{it.title}</div>
                          </td>
                          <td className="px-3 py-2 text-white/60">{it.authority_lane || "—"}</td>
                          <td className="px-3 py-2 text-white/70 whitespace-nowrap">
                            {formatDate(it.slice_started_at)} → {it.slice_ended_at ? formatDate(it.slice_ended_at) : <span className="text-indigo-300">now</span>}
                          </td>
                          <td className="px-3 py-2 text-white text-right font-medium">{it.working_days_held}</td>
                          <td className="px-3 py-2 text-white/60 whitespace-nowrap">{formatDate(it.original_due_date)}</td>
                          <td className="px-3 py-2 text-white/60">{it.current_status.replace(/_/g, " ")}</td>
                          <td className="px-3 py-2 text-center">
                            {it.caused_drop && <span className="text-red-400">●</span>}
                          </td>
                          <td className="px-3 py-2 text-right whitespace-nowrap">
                            <Link href={`/action-items/${it.id}`}
                              className="inline-flex items-center gap-1 text-cyan-300 hover:text-cyan-200 text-xs"
                              data-testid={`link-detail-${it.id}`}>
                              Open <ExternalLink className="w-3 h-3" />
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

// ─── Page shell ──────────────────────────────────────────────────────────────

type TabKey = "overdue" | "md-history" | "employee-dossier" | "department-dossier";

type DepartmentEmployeeRow = {
  person: string;
  holding: number; holding_overdue: number; owns_open: number; received_from_other: number;
  total_slices: number; items_touched: number;
  cleared_on_time: number; cleared_late: number; handed_off_clean: number; handed_off_late: number;
  received_late: number; still_holding_overdue: number; still_holding_in_flight: number;
  expired_in_their_hands: number; drop_count: number; reschedules_caused: number;
  avg_working_days_held: number; on_time_rate: number | null;
};

type DepartmentDossier = {
  department: string; member_count: number; members: string[];
  range: { from: string; to: string }; generated_at: string;
  live: {
    holding: number; holding_overdue: number; received_from_other: number;
    owns_open: number; distinct_open_items: number;
    by_status: { status: string; count: number }[];
  };
  aggregates: {
    total_items_touched: number; total_slices: number;
    cleared_on_time: number; cleared_late: number;
    still_holding_overdue: number; still_holding_in_flight: number;
    handed_off_clean: number; handed_off_late: number;
    received_late: number; expired_in_their_hands: number;
    avg_working_days_held: number; drop_count: number; reschedules_caused: number;
    on_time_rate: number | null;
  };
  per_employee: DepartmentEmployeeRow[];
};

function rateColor(rate: number | null): string {
  if (rate === null) return "text-white/40";
  if (rate >= 90) return "text-emerald-400";
  if (rate >= 75) return "text-amber-400";
  return "text-red-400";
}

function DepartmentDossierTab() {
  const [department, setDepartment] = useState<string>("");
  const [from, setFrom] = useState<string>(shiftIsoDays(365));
  const [to, setTo] = useState<string>(todayIso());
  const [submitted, setSubmitted] = useState<{ department: string; from: string; to: string } | null>(null);

  const { data: deptList } = useQuery<{ departments: { id: string; name: string; memberCount: number }[] }>({
    queryKey: ["/api/dossier-departments"],
    queryFn: async () => {
      const res = await fetch("/api/dossier-departments", { credentials: "include" });
      if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
      return res.json();
    },
  });
  const departments = deptList?.departments ?? [];

  const { data, isLoading, error } = useQuery<DepartmentDossier>({
    queryKey: ["/api/department-dossier", submitted?.department, submitted?.from, submitted?.to],
    enabled: !!submitted,
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set("department", submitted!.department);
      if (submitted!.from) params.set("from", submitted!.from);
      if (submitted!.to) params.set("to", submitted!.to);
      const res = await fetch(`/api/department-dossier?${params.toString()}`, { credentials: "include" });
      if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
      return res.json();
    },
  });

  function generate() {
    if (!department) return;
    setSubmitted({ department, from, to });
  }

  function presetRange(kind: "30d" | "90d" | "ytd" | "12m") {
    if (kind === "30d") { setFrom(shiftIsoDays(30)); setTo(todayIso()); }
    else if (kind === "90d") { setFrom(shiftIsoDays(90)); setTo(todayIso()); }
    else if (kind === "ytd") { setFrom(startOfYearIso()); setTo(todayIso()); }
    else if (kind === "12m") { setFrom(shiftIsoDays(365)); setTo(todayIso()); }
  }

  const agg = data?.aggregates;

  return (
    <div className="space-y-5">
      {/* Controls */}
      <Card className="bg-white/5 border-white/10">
        <CardContent className="p-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr_1fr_auto] gap-3 items-end">
            <div className="space-y-1.5">
              <label className="text-white/60 text-xs">Department</label>
              <Select value={department} onValueChange={setDepartment}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="select-department">
                  <SelectValue placeholder="Select a department…" />
                </SelectTrigger>
                <SelectContent className="max-h-80 overflow-y-auto">
                  {departments.map((d) => (
                    <SelectItem key={d.id} value={d.name} data-testid={`option-department-${d.name}`}>
                      {d.name} · {d.memberCount} {d.memberCount === 1 ? "person" : "people"}
                    </SelectItem>
                  ))}
                  {departments.length === 0 && (
                    <SelectItem value="__none__" disabled>No departments found</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <label className="text-white/60 text-xs">From</label>
              <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
                className="bg-white/5 border-white/10 text-white" data-testid="input-department-from" />
            </div>
            <div className="space-y-1.5">
              <label className="text-white/60 text-xs">To</label>
              <Input type="date" value={to} onChange={(e) => setTo(e.target.value)}
                className="bg-white/5 border-white/10 text-white" data-testid="input-department-to" />
            </div>
            <Button onClick={generate} disabled={!department} data-testid="button-department-generate"
              className="bg-cyan-600 hover:bg-cyan-500 text-white">
              Generate
            </Button>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-white/40 text-xs mr-1">Quick range:</span>
            <Button size="sm" variant="outline" className="h-7 px-2 text-xs bg-white/5 border-white/10 text-white/70 hover:bg-white/10"
              onClick={() => presetRange("30d")} data-testid="button-dept-range-30d">Last 30d</Button>
            <Button size="sm" variant="outline" className="h-7 px-2 text-xs bg-white/5 border-white/10 text-white/70 hover:bg-white/10"
              onClick={() => presetRange("90d")} data-testid="button-dept-range-90d">Last 90d</Button>
            <Button size="sm" variant="outline" className="h-7 px-2 text-xs bg-white/5 border-white/10 text-white/70 hover:bg-white/10"
              onClick={() => presetRange("ytd")} data-testid="button-dept-range-ytd">YTD</Button>
            <Button size="sm" variant="outline" className="h-7 px-2 text-xs bg-white/5 border-white/10 text-white/70 hover:bg-white/10"
              onClick={() => presetRange("12m")} data-testid="button-dept-range-12m">Last 12 months</Button>
          </div>
        </CardContent>
      </Card>

      {!submitted && (
        <Card className="bg-white/5 border-white/10">
          <CardContent className="p-8 text-center text-white/50">
            Pick a department and a date range, then press <strong>Generate</strong> to build the team scorecard.
          </CardContent>
        </Card>
      )}

      {submitted && isLoading && (
        <Card className="bg-white/5 border-white/10">
          <CardContent className="p-8 flex items-center justify-center gap-2 text-white/50">
            <Loader2 className="w-4 h-4 animate-spin text-cyan-400" /> Building department scorecard…
          </CardContent>
        </Card>
      )}

      {submitted && error && (
        <Card className="bg-white/5 border-white/10">
          <CardContent className="p-6 text-red-300 text-sm">
            Couldn't build the report: {String((error as Error).message)}
          </CardContent>
        </Card>
      )}

      {data && agg && (
        <>
          {/* Headline KPI band */}
          <Card className="bg-white/5 border-white/10" data-testid="card-department-kpi">
            <CardHeader className="pb-2">
              <CardTitle className="text-base text-white flex items-center gap-2">
                <Building2 className="w-4 h-4 text-cyan-400" />
                {data.department}
                <span className="text-xs text-white/40 font-normal ml-2">
                  {data.member_count} {data.member_count === 1 ? "person" : "people"} · {data.range.from} → {data.range.to}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                <div className="rounded border border-white/10 bg-white/5 p-3 text-center col-span-2 sm:col-span-1">
                  <div className="text-xs uppercase tracking-wide text-white/50 flex items-center justify-center gap-1">
                    <Target className="w-3 h-3" /> On-time rate
                  </div>
                  <div className={`text-3xl font-bold mt-1 ${rateColor(agg.on_time_rate)}`} data-testid="dept-on-time-rate">
                    {agg.on_time_rate === null ? "—" : `${agg.on_time_rate}%`}
                  </div>
                </div>
                {[
                  { l: "Items touched", v: agg.total_items_touched, c: "text-white", t: "items-touched" },
                  { l: "Cleared on time", v: agg.cleared_on_time + agg.handed_off_clean, c: "text-emerald-400", t: "cleared-clean" },
                  { l: "Late", v: agg.cleared_late + agg.handed_off_late, c: "text-amber-400", t: "late" },
                  { l: "Drops", v: agg.drop_count, c: agg.drop_count > 0 ? "text-red-400" : "text-white/60", t: "drops" },
                  { l: "Avg working-days held", v: agg.avg_working_days_held, c: "text-white", t: "avg-wd" },
                ].map((s) => (
                  <div key={s.l} className="rounded border border-white/10 bg-white/5 p-3 text-center">
                    <div className="text-xs uppercase tracking-wide text-white/50">{s.l}</div>
                    <div className={`text-2xl font-semibold mt-1 ${s.c}`} data-testid={`dept-${s.t}`}>{s.v}</div>
                  </div>
                ))}
              </div>
              <p className="text-white/30 text-xs mt-2">
                On-time rate = clean conclusions ÷ (clean + late + overdue) over the selected range. Inherited-late
                and still-in-flight items are excluded.
              </p>
            </CardContent>
          </Card>

          {/* Live workload band */}
          <Card className="bg-white/5 border-white/10" data-testid="card-department-live">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-white flex items-center gap-2">
                <Gauge className="w-4 h-4 text-cyan-400" /> Workload now (whole department)
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                {[
                  { l: "Open items held", v: data.live.distinct_open_items, c: "text-white", t: "live-holding" },
                  { l: "Held (overdue)", v: data.live.holding_overdue, c: data.live.holding_overdue > 0 ? "text-red-400" : "text-white/60", t: "live-overdue" },
                  { l: "Received from outside", v: data.live.received_from_other, c: "text-indigo-400", t: "live-received" },
                  { l: "Owns (open)", v: data.live.owns_open, c: "text-white", t: "live-owns" },
                ].map((s) => (
                  <div key={s.l} className="rounded border border-white/10 bg-white/5 p-3 text-center">
                    <div className="text-xs uppercase tracking-wide text-white/50">{s.l}</div>
                    <div className={`text-2xl font-semibold mt-1 ${s.c}`} data-testid={`dept-${s.t}`}>{s.v}</div>
                  </div>
                ))}
              </div>
              {data.live.by_status.length > 0 && (
                <div className="flex flex-wrap items-center gap-2 mt-3">
                  <span className="text-white/40 text-xs mr-1">Held by status:</span>
                  {data.live.by_status.map((b) => (
                    <Badge key={b.status} variant="outline" className="bg-white/5 border-white/15 text-white/70 text-xs"
                      data-testid={`dept-status-${b.status}`}>
                      {prettyStatus(b.status)} · {b.count}
                    </Badge>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Per-employee league table */}
          <Card className="bg-white/5 border-white/10" data-testid="card-department-league">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-white flex items-center gap-2">
                <Users className="w-4 h-4 text-cyan-400" /> Per-person breakdown
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              {data.per_employee.length === 0 ? (
                <div className="text-white/50 text-sm py-2">No active members in this department.</div>
              ) : (
                <div className="overflow-x-auto rounded border border-white/10">
                  <table className="w-full text-xs" data-testid="table-department-league">
                    <thead>
                      <tr className="bg-white/5 text-left text-white/60 uppercase tracking-wide">
                        <th className="px-3 py-2 font-medium">Person</th>
                        <th className="px-3 py-2 font-medium text-center">On-time</th>
                        <th className="px-3 py-2 font-medium text-center">Holding</th>
                        <th className="px-3 py-2 font-medium text-center">Overdue</th>
                        <th className="px-3 py-2 font-medium text-center">Owns</th>
                        <th className="px-3 py-2 font-medium text-center">On time</th>
                        <th className="px-3 py-2 font-medium text-center">Late</th>
                        <th className="px-3 py-2 font-medium text-center">Drops</th>
                        <th className="px-3 py-2 font-medium text-center">Avg WD</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.per_employee.map((r) => (
                        <tr key={r.person} className="border-t border-white/5 hover:bg-white/5" data-testid={`row-dept-${r.person}`}>
                          <td className="px-3 py-2 text-white/90">{r.person}</td>
                          <td className={`px-3 py-2 text-center font-semibold ${rateColor(r.on_time_rate)}`}>
                            {r.on_time_rate === null ? "—" : `${r.on_time_rate}%`}
                          </td>
                          <td className="px-3 py-2 text-center text-white/80">{r.holding}</td>
                          <td className={`px-3 py-2 text-center ${r.holding_overdue > 0 ? "text-red-400 font-semibold" : "text-white/50"}`}>{r.holding_overdue}</td>
                          <td className="px-3 py-2 text-center text-white/80">{r.owns_open}</td>
                          <td className="px-3 py-2 text-center text-emerald-300">{r.cleared_on_time + r.handed_off_clean}</td>
                          <td className="px-3 py-2 text-center text-amber-300">{r.cleared_late + r.handed_off_late}</td>
                          <td className={`px-3 py-2 text-center ${r.drop_count > 0 ? "text-red-400 font-semibold" : "text-white/50"}`}>{r.drop_count}</td>
                          <td className="px-3 py-2 text-center text-white/70">{r.avg_working_days_held}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

export default function HrDossierPage() {
  const { containerClass } = useLayout();
  const { hasPermission } = useAuth();
  const canSeeMdHistory = hasPermission("view_md_review_history");
  const canSeeEmployeeDossier = hasPermission("view_employee_dossier");

  // Read initial tab + person + range from the URL so deep-links from the
  // KPI dashboard and from Overdue holder names land on the right report.
  const search = typeof window !== "undefined" ? window.location.search : "";
  const params = new URLSearchParams(search);
  const urlTab = params.get("tab");
  const initialTab: TabKey = (urlTab === "md-history" && canSeeMdHistory) ? "md-history"
    : (urlTab === "employee-dossier" && canSeeEmployeeDossier) ? "employee-dossier"
    : (urlTab === "department-dossier" && canSeeEmployeeDossier) ? "department-dossier"
    : "overdue";
  const initialPerson = params.get("person") || "";
  const initialFrom = params.get("from") || "";
  const initialTo = params.get("to") || "";

  const [tab, setTab] = useState<TabKey>(initialTab);

  return (
    <div className={`${containerClass} mx-auto px-6 py-8 space-y-5`}>
      <div>
        <h1 className="text-2xl font-semibold text-white tracking-tight flex items-center gap-2" data-testid="text-page-title">
          <Users className="w-6 h-6 text-cyan-400" />
          HR Dossier
        </h1>
        <p className="text-sm text-white/60 mt-1 max-w-2xl">
          Two views into how the organisation is handling its action items: who is currently sitting on
          overdue work, and how the senior-leadership review tier has been processing items sent up to it.
        </p>
      </div>

      <Tabs value={tab} onValueChange={(v) => setTab(v as TabKey)}>
        <TabsList className="bg-white/5 border border-white/10">
          <TabsTrigger
            value="overdue"
            className="data-[state=active]:bg-white/10 data-[state=active]:text-white"
            data-testid="tab-overdue"
          >
            <Users className="w-4 h-4 mr-2" />
            Overdue Dossier
          </TabsTrigger>
          {canSeeMdHistory && (
            <TabsTrigger
              value="md-history"
              className="data-[state=active]:bg-white/10 data-[state=active]:text-white"
              data-testid="tab-md-history"
            >
              <History className="w-4 h-4 mr-2" />
              MD Review History
            </TabsTrigger>
          )}
          {canSeeEmployeeDossier && (
            <TabsTrigger
              value="employee-dossier"
              className="data-[state=active]:bg-white/10 data-[state=active]:text-white"
              data-testid="tab-employee-dossier"
            >
              <UserCheck className="w-4 h-4 mr-2" />
              Employee Dossier
            </TabsTrigger>
          )}
          {canSeeEmployeeDossier && (
            <TabsTrigger
              value="department-dossier"
              className="data-[state=active]:bg-white/10 data-[state=active]:text-white"
              data-testid="tab-department-dossier"
            >
              <Building2 className="w-4 h-4 mr-2" />
              Department Dossier
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="overdue" className="mt-5">
          <OverdueDossierTab />
        </TabsContent>
        {canSeeMdHistory && (
          <TabsContent value="md-history" className="mt-5">
            <MdReviewHistoryTab />
          </TabsContent>
        )}
        {canSeeEmployeeDossier && (
          <TabsContent value="employee-dossier" className="mt-5">
            <EmployeeDossierTab initialPerson={initialPerson} initialFrom={initialFrom} initialTo={initialTo} />
          </TabsContent>
        )}
        {canSeeEmployeeDossier && (
          <TabsContent value="department-dossier" className="mt-5">
            <DepartmentDossierTab />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
